import { Suspense, lazy, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type { DisruptionDetail } from '@/lib/disruptions/types';
import { badgeFromDelay, badgeFromFlightStatus, flightOptionsOf, leadDelay } from '@/lib/delay/format';
import { useDisruptionDelays } from '@/lib/delay/use-delay';
import { DelayBadge } from './delay-badge';
import type { IDelayDialogPreset } from './disruption-delay-dialog';

// The disruption header's flight-delay slot (flight-delay U1): the badge (STD → ETD
// +X, "rebooking on hold") and the trigger of the delay dialog, where the operator
// reads the timeline and declares / updates the ETD.
//
// The badge reads the disruption's timelines when the row's `flightStatus@v1` says a
// delay summary exists (one read, then WS-merged); otherwise it shows what the row
// says (e.g. "Cancelled"), or nothing. A row that is more severe than the timelines
// (a cancelled flight next to a delayed one) keeps the more severe badge, and any
// flight's out-of-policy report shows «Fuera de política» (delay-limits U2).
// The dialog body is lazy — nothing of the panel loads until it is opened. `DelayDialog`
// is shared with the out-of-policy alert, which opens it preset (flight + kind).

const DisruptionDelayDialog = lazy(() =>
  import('./disruption-delay-dialog').then((m) => ({ default: m.DisruptionDelayDialog })),
);

export function HeaderDelay({ detail }: { detail: DisruptionDetail }) {
  const { t } = useTranslation('disruptions');
  const [open, setOpen] = useState(false);
  const status = detail.flightStatus;
  const summarized = typeof status?.maxDelayMinutes === 'number';
  const delays = useDisruptionDelays(detail.urn, summarized || open);
  const hasFlights = useMemo(() => flightOptionsOf(detail.cases ?? []).length > 0, [detail.cases]);

  const lead = delays.data ? leadDelay(delays.data) : null;
  const fromLead = lead ? badgeFromDelay(lead.lead, lead.others) : null;
  const fromStatus = badgeFromFlightStatus(status);
  const picked = fromLead && !(fromStatus?.tone === 'error' && fromLead.tone !== 'error') ? fromLead : (fromStatus ?? fromLead);
  // The timelines are fresher than the row: they decide the flag once read.
  const outOfPolicy = delays.data ? delays.data.some((d) => !!d.outOfPolicy) : status?.outOfPolicy === true;
  const model = picked && { ...picked, outOfPolicy };

  if (!hasFlights && !model) return null;
  return (
    <span className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-2" data-testid="header-delay">
      <DelayBadge model={model} size="md" />
      <DelayDialog detail={detail} open={open} onOpenChange={setOpen}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-11 gap-1 px-3 text-xs sm:h-7 sm:px-2"
          aria-label={t('delay.triggerAria')}
          data-testid="delay-panel-trigger"
        >
          <Clock3 className="h-3.5 w-3.5" aria-hidden />
          {t('delay.trigger')}
        </Button>
      </DelayDialog>
    </span>
  );
}

/** The delay dialog: the header's trigger (`children`) opens it, or a caller holding `open` (preset). */
export function DelayDialog({
  detail,
  open,
  onOpenChange,
  preset,
  children,
}: {
  detail: DisruptionDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preset?: IDelayDialogPreset | undefined;
  children?: ReactNode;
}) {
  const { t } = useTranslation('disruptions');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent className="max-h-[90dvh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('delay.dialog.title')}</DialogTitle>
          <DialogDescription>{t('delay.dialog.description')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <Suspense fallback={<Skeleton className="h-40 w-full" />}>
            <DisruptionDelayDialog disruptionUrn={detail.urn} cases={detail.cases ?? []} preset={preset} />
          </Suspense>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
